import { useCallback, useEffect, useState } from 'react';
import type { PaginationMetaI } from 'types';
import useDebounce from 'Hooks/useDebounce';
import instructorsApi from 'Api/axios/instructors-api';
import { Instructor, InstructorDraft } from '../data/instructors';

export type InstructorFilter = 'all' | 'active' | 'inactive';

/** Resultado de una operación de guardado/borrado, para que la UI reaccione. */
export interface SaveResult {
  ok: boolean;
  /** El backend respondió 409 por correo duplicado. */
  emailTaken?: boolean;
  /** Mensaje de error legible (cuando `ok` es false). */
  message?: string;
}

const FALLBACK_ERROR = 'No se pudo completar la operación. Intenta de nuevo.';
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Estado y operaciones del catálogo de instructores contra `/api/instructors`.
 *
 * Búsqueda, filtro por estatus y paginación son del lado del servidor. La
 * búsqueda se debouncea y cualquier cambio de búsqueda o filtro regresa a la
 * primera página. Los conteos de las pestañas vienen de `meta` (respetan la
 * búsqueda, ignoran el filtro de estatus).
 */
export const useInstructors = () => {
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [counts, setCounts] = useState({ all: 0, active: 0, inactive: 0 });
  const [pagination, setPagination] = useState<PaginationMetaI | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearchRaw] = useState('');
  const [filter, setFilterRaw] = useState<InstructorFilter>('all');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const fetchPage = useCallback(async (targetPage: number, query: string, status: InstructorFilter) => {
    setLoading(true);
    const res = await instructorsApi.readAll({
      search: query.trim() || undefined,
      status,
      page: targetPage,
      pageSize: PAGE_SIZE,
    });
    setInstructors(res.data || []);
    const meta = res.meta as { total?: number; active?: number; inactive?: number } | null;
    setCounts({
      all: Number(meta?.total ?? 0),
      active: Number(meta?.active ?? 0),
      inactive: Number(meta?.inactive ?? 0),
    });
    const pag = res.pagination ?? null;
    setPagination(pag);
    setLoading(false);
    // Si la página quedó fuera de rango (p. ej. tras borrar el último de la última
    // página), retrocede a la última página válida.
    if (pag && pag.totalPages > 0 && targetPage > pag.totalPages) {
      setPage(pag.totalPages);
    }
  }, []);

  // Carga al cambiar página, búsqueda (debounced) o filtro.
  useEffect(() => {
    fetchPage(page, debouncedSearch, filter);
  }, [page, debouncedSearch, filter, fetchPage]);

  // Cambiar búsqueda o filtro regresa a la primera página (evita el doble fetch
  // reseteando la página en el mismo evento, no en un efecto aparte).
  const setSearch = (value: string) => {
    setSearchRaw(value);
    setPage(1);
  };
  const setFilter = (value: InstructorFilter) => {
    setFilterRaw(value);
    setPage(1);
  };

  const refresh = () => fetchPage(page, debouncedSearch, filter);

  const toResult = (error: string | null, code?: string | null): SaveResult => {
    if (error == null) return { ok: true };
    if (code === 'email_taken') return { ok: false, emailTaken: true, message: error };
    return { ok: false, message: error || FALLBACK_ERROR };
  };

  const create = async (draft: InstructorDraft): Promise<SaveResult> => {
    const res = await instructorsApi.create(draft);
    const result = toResult(res.error, res.code);
    if (result.ok) await refresh();
    return result;
  };

  const update = async (id: string, draft: InstructorDraft): Promise<SaveResult> => {
    const res = await instructorsApi.update(id, draft);
    const result = toResult(res.error, res.code);
    if (result.ok) await refresh();
    return result;
  };

  const remove = async (id: string): Promise<SaveResult> => {
    const res = await instructorsApi.remove(id);
    const result = toResult(res.error, res.code);
    if (result.ok) await refresh();
    return result;
  };

  return {
    instructors,
    counts,
    pagination,
    page,
    goToPage: setPage,
    loading,
    search,
    setSearch,
    filter,
    setFilter,
    create,
    update,
    remove,
  };
};

export default useInstructors;
